import { useMemo, useState, type ReactNode } from "react";
import { Dialog, DialogTrigger, Pressable } from "react-aria-components";
import { Check, ChevronDown, Globe, Search } from "lucide-react";
import { KitPopover } from "@/app/routes/dashboard/components/kit/Popover";
import { BrandMark } from "@/app/routes/dashboard/components/kit/BrandPicker";
import type { Brand } from "@/hooks/useGetBrands";
import { cn } from "@/utils/helpers";

/*
 * References / Brand picker (library, style B). Chrome frost, radius 12, 304
 * wide: a mono head (SAVE FOR · 7 BRANDS), a search once there are more than
 * five brands, Everyone first with its note, a hairline, then the brands with
 * their tile. The current one is tinted and checked; Move to a brand marks the
 * brand it is in now "Here now".
 */

const SEARCH_FROM = 6;

export function ReferenceBrandPickerPanel({
  label,
  brands,
  value,
  allowEveryone,
  hereId,
  onPick,
}: {
  label: string;
  brands: Brand[];
  /** null = Everyone. */
  value: string | null | undefined;
  allowEveryone: boolean;
  /** Move to a brand: where it is now ("Here now"); picking it does nothing. */
  hereId?: string | null;
  onPick: (brandId: string | null) => void;
}) {
  const [q, setQ] = useState("");
  const shown = useMemo(() => {
    const term = q.trim().toLowerCase();
    return term ? brands.filter((b) => b.name.toLowerCase().includes(term)) : brands;
  }, [brands, q]);
  const isHere = (id: string | null) => hereId !== undefined && hereId === id;

  return (
    <div className="ref-brand-picker">
      <div className="ref-brand-head">
        <span>{label}</span>
        <span className="text-co-faint">
          {brands.length} {brands.length === 1 ? "brand" : "brands"}
        </span>
      </div>
      {brands.length >= SEARCH_FROM ? (
        <label className="ref-brand-search co-glass-control">
          <Search aria-hidden className="size-3.5 text-co-faint" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Find a brand"
            aria-label="Find a brand"
          />
        </label>
      ) : null}
      <div className="ref-brand-list" role="listbox" aria-label={label}>
        {allowEveryone && !q ? (
          <>
            <button
              type="button"
              role="option"
              aria-selected={value === null}
              className={cn("ref-brand-row ref-brand-row--everyone", value === null && "is-current")}
              onClick={() => !isHere(null) && onPick(null)}
            >
              <span className="ref-brand-globe" aria-hidden>
                <Globe className="size-3.5" />
              </span>
              <span className="flex min-w-0 flex-1 flex-col gap-0.5 text-start">
                <span className="text-[13px] font-semibold text-co-text">Everyone</span>
                <span className="text-[11.5px] text-co-muted">
                  {isHere(null) ? "Here now" : "Every brand can use it"}
                </span>
              </span>
              {value === null ? <Check aria-hidden className="size-3.5 text-co-accent" /> : null}
            </button>
            <span className="ref-brand-rule" aria-hidden />
          </>
        ) : null}
        {shown.map((brand) => {
          const current = value === brand.id;
          return (
            <button
              key={brand.id}
              type="button"
              role="option"
              aria-selected={current}
              className={cn("ref-brand-row", current && "is-current")}
              onClick={() => !isHere(brand.id) && onPick(brand.id)}
            >
              <BrandMark brand={brand} size={28} />
              <span className={cn("min-w-0 flex-1 truncate text-start text-[13px] text-co-text", current ? "font-semibold" : "font-medium")}>
                {brand.name}
              </span>
              {isHere(brand.id) ? <span className="text-[11.5px] text-co-muted">Here now</span> : null}
              {current ? <Check aria-hidden className="size-3.5 text-co-accent" /> : null}
            </button>
          );
        })}
        {shown.length === 0 ? <p className="px-2 py-3 text-[13px] text-co-muted">No brand matches.</p> : null}
      </div>
    </div>
  );
}

/** The FOR select: a 36px glass capsule with the brand's tile and name, opening the picker. */
export function ReferenceBrandSelect({
  label,
  brands,
  value,
  allowEveryone,
  onChange,
  className,
}: {
  label: string;
  brands: Brand[];
  value: string | null;
  allowEveryone: boolean;
  onChange: (brandId: string | null) => void;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const brand = value ? brands.find((b) => b.id === value) : null;
  return (
    <DialogTrigger isOpen={open} onOpenChange={setOpen}>
      <Pressable>
        <button type="button" className={cn("ref-brand-select co-glass-control", className)} aria-label={`${label}: ${brand?.name ?? "Everyone"}`}>
          {brand ? (
            <BrandMark brand={brand} size={22} />
          ) : (
            <span className="ref-brand-globe ref-brand-globe--sm" aria-hidden>
              <Globe className="size-3" />
            </span>
          )}
          <span className="truncate">{brand?.name ?? "Everyone"}</span>
          <ChevronDown aria-hidden className="size-[13px] text-co-muted" />
        </button>
      </Pressable>
      <KitPopover placement="bottom end" className="ref-chrome-pop w-[304px] p-0">
        <Dialog aria-label={label} className="outline-none">
          <ReferenceBrandPickerPanel
            label={label}
            brands={brands}
            value={value}
            allowEveryone={allowEveryone}
            onPick={(id) => {
              onChange(id);
              setOpen(false);
            }}
          />
        </Dialog>
      </KitPopover>
    </DialogTrigger>
  );
}

/** A labelled row: the mono caption at the left, the control at the right. */
export function ReferenceFieldRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="ref-mono-label">{label}</span>
      {children}
    </div>
  );
}
